import { useEffect, useState } from "react";
import { ExternalLink, ShieldCheck, AlertTriangle, Loader2, Copy, CheckCircle2, Globe, Code2, Link2, Smartphone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { authFetch } from "../lib/api";
import { siteBaseUrl } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_LABELS = {
  free: "Free",
  basic: "Basic",
  pro: "Pro",
  enterprise: "Enterprise",
};

/**
 * Business setting card: Connect Existing Website.
 *
 * Free      : URL save only, NOT publicly visible.
 * Trial/Basic: public CTA + click stats.
 * Pro+      : adds verification + badge snippet.
 */

// Hoisted so it's stable across renders (react/no-unstable-nested-components).
function StatusBadge({ status }) {
  if (status === "verified") return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200"><ShieldCheck className="w-3 h-3 mr-1" /> Website ownership verified</Badge>;
  if (status === "pending")  return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Pending verification</Badge>;
  if (status === "failed")   return <Badge className="bg-red-100 text-red-800 border-red-200">Verification failed</Badge>;
  return null;
}

export default function ConnectExistingWebsite({ expanded = false }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("Visit official website");
  const [copied, setCopied] = useState(false);
  const [integrationSaving, setIntegrationSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/business/external-website`);
      const d = await r.json();
      setState(d);
      setUrl(d.url || "");
      setLabel(d.button_label || "Visit official website");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 text-slate-500 flex items-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>
  );
  if (!state) return null;

  const caps = state.capabilities || {};
  const plan = caps.effective_plan || "free";
  const integration = state.booking_integration || {};
  const publicIdentifier = state.public_slug || integration.public_integration_id;
  const hostedUrl = publicIdentifier ? `${siteBaseUrl()}/book/${publicIdentifier}` : "";
  const embedSnippet = integration.public_integration_id
    ? `<div data-confirmph-booking="${integration.public_integration_id}"></div>\n<script async src="${siteBaseUrl()}/embed/v1/widget.js"></script>`
    : "";

  const save = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const r = await authFetch(`${API}/business/external-website`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url, button_label: label }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || `Save failed (${r.status})`);
      }
      const d = await r.json();
      setState(d); setUrl(d.url); setLabel(d.button_label);
      setSuccess("Saved.");
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const startVerify = async () => {
    setVerifying(true); setError(null); setSuccess(null);
    try {
      const r = await authFetch(`${API}/business/external-website/verify`,
                                  { method: "POST" });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail?.message || e.detail || `Failed (${r.status})`);
      }
      setState(await r.json());
      setSuccess("Verification token issued. Add the meta tag to your homepage, then click Check.");
    } catch (e) {
      setError(String(e.message || e));
    } finally { setVerifying(false); }
  };

  const checkVerify = async () => {
    setVerifying(true); setError(null); setSuccess(null);
    try {
      const r = await authFetch(`${API}/business/external-website/verify/check`,
                                  { method: "POST" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail?.message || d.detail || `Failed (${r.status})`);
      setState(d);
      if (d.ok) setSuccess("Website ownership verified.");
      else setError(d.detail || "Verification failed.");
    } catch (e) {
      setError(String(e.message || e));
    } finally { setVerifying(false); }
  };

  const saveIntegration = async (patch = {}) => {
    setIntegrationSaving(true); setError(null); setSuccess(null);
    try {
      const r = await authFetch(`${API}/business/external-website/integration`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail?.message || d.detail || `Failed (${r.status})`);
      setState(d);
      setSuccess("Integration shell settings saved.");
    } catch (e) {
      setError(String(e.message || e));
    } finally { setIntegrationSaving(false); }
  };

  const metaTag = state.verification_token
    ? `<meta name="${state.verification_meta_name}" content="${state.verification_token}" />`
    : "";

  const badgeSnippet = `<a href="https://confirm.ph/business/${(window.__cph_company_id || "")}" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:6px;padding:6px 12px;border-radius:6px;background:#1B4332;color:#fff;text-decoration:none;font-family:system-ui;font-size:13px;font-weight:600;">View our ConfirmPh profile</a>`;

  const copy = async (txt) => {
    try { await navigator.clipboard.writeText(txt); setCopied(true);
           setTimeout(() => setCopied(false), 1500); } catch {}
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4"
         data-testid="connect-existing-website">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-semibold text-slate-900">
            <Globe className="w-5 h-5 text-emerald-700" />
            Connect Existing Website
          </div>
          <p className="text-sm text-slate-600 mt-1">
            <strong>Already have a website? Connect it to ConfirmPh.</strong><br />
            ConfirmPh does not replace your website: it adds discovery,
            booking confirmation, reviews, and profile signals on top.
          </p>
        </div>
        <Badge variant="outline" className="text-[10px] whitespace-nowrap">
          Your plan: {PLAN_LABELS[plan] || plan}
        </Badge>
      </div>

      {expanded && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 space-y-4" data-testid="ew-booking-integration">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-semibold text-slate-900 flex items-center gap-2">
                <Code2 className="w-4 h-4 text-emerald-700" /> Booking integration
              </div>
              <p className="text-xs text-slate-600 mt-1 max-w-2xl">
                The hosted booking page submits real requests through your existing booking workflow. The embedded widget shows your real vehicles and availability and collects an inquiry in place: completing an actual reservation hands off to the hosted page, kept that way until a dedicated widget origin exists.
              </p>
            </div>
            <div className="flex flex-wrap gap-1.5 text-[11px]">
              <StatusPill ok={Boolean(state.url)} label={state.url ? "Connected" : "Not connected"} />
              <StatusPill ok={state.verification_status === "verified"} label={state.verification_status === "verified" ? "Verified" : "Not verified"} />
              <StatusPill ok={integration.widget_enabled} label={integration.widget_enabled ? "Widget enabled" : "Widget disabled"} />
            </div>
          </div>

          {!integration.public_integration_id ? (
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="font-medium text-sm">Prepare secure integration identifiers</div>
              <p className="text-xs text-slate-500 mt-1">This creates an opaque public identifier. It is not an API key and does not enable booking submission.</p>
              <Button className="mt-3" size="sm" disabled={!state.url || integrationSaving} onClick={() => saveIntegration({ hosted_enabled: false, widget_enabled: false })} data-testid="ew-integration-prepare">
                {integrationSaving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />} Prepare integration
              </Button>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 gap-3">
                <IntegrationCard
                  icon={Link2}
                  title="Hosted booking page"
                  description="For websites that prefer a link or button instead of embedded code."
                  enabled={integration.hosted_enabled}
                  disabled={integrationSaving}
                  onToggle={() => saveIntegration({ hosted_enabled: !integration.hosted_enabled })}
                  testId="ew-hosted-toggle"
                >
                  {integration.hosted_enabled && !integration.hosted_booking_submission_enabled && (
                    <p className="text-xs text-amber-800" data-testid="ew-hosted-not-entitled-warning">
                      This is on, but real bookings won't work yet: hosted booking requires
                      in-app bookings enabled on a Pro or Enterprise plan. The page will show
                      as unavailable to customers until that's set up.
                    </p>
                  )}
                  {hostedUrl && <CopyRow value={hostedUrl} onCopy={copy} label="Hosted booking URL" testId="ew-copy-hosted" />}
                </IntegrationCard>

                <IntegrationCard
                  icon={Code2}
                  title="Embed booking widget"
                  description="A sandboxed iframe shows real vehicles/availability and takes an inquiry. It hands off to the hosted page to complete an actual reservation until a dedicated widget origin exists."
                  enabled={integration.widget_enabled}
                  disabled={integrationSaving || state.verification_status !== "verified"}
                  onToggle={() => saveIntegration({ widget_enabled: !integration.widget_enabled })}
                  testId="ew-widget-toggle"
                >
                  {state.verification_status !== "verified" ? (
                    <p className="text-xs text-amber-800">Verify ownership of the connected domain before enabling the widget shell.</p>
                  ) : embedSnippet ? (
                    <CopyRow value={embedSnippet} onCopy={copy} label="Embed snippet" testId="ew-copy-embed" multiline />
                  ) : null}
                </IntegrationCard>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="ew-installation-instructions">
                <div>
                  <div className="font-semibold text-sm">Installation instructions</div>
                  <p className="text-xs text-slate-500 mt-0.5">Paste the generated code only on the verified website. Availability and pricing will be validated by ConfirmPH after final integration; hidden website values are never authoritative.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <InstallGuide title="WordPress" steps={["Add a Custom HTML block.", "Paste the embed snippet and publish.", "If scripts are stripped, use the hosted URL button instead."]} />
                  <InstallGuide title="Wix" steps={["Choose Embed Code → Embed HTML.", "Paste the snippet and use full available width.", "Check desktop and mobile layouts before publishing."]} />
                  <InstallGuide title="Squarespace" steps={["Add a Code block.", "Paste the snippet where bookings should appear.", "Use the hosted URL if script injection is unavailable."]} />
                  <InstallGuide title="Custom HTML" steps={["Add the mount element where the widget belongs.", "Load the versioned script once with async.", "Do not alter or treat the public identifier as a secret."]} />
                </div>
                <a href={`${siteBaseUrl()}/widget-test.html?id=${encodeURIComponent(integration.public_integration_id)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 hover:underline" data-testid="ew-widget-test-link">
                  <Smartphone className="w-4 h-4" /> Open widget test page
                </a>
              </div>

              <div className="text-[11px] text-slate-500 rounded-lg border border-dashed border-slate-300 p-3">
                Current plan display: <strong>{PLAN_LABELS[plan] || plan}</strong>. Website Integration is included with your Pro or Enterprise online-booking plan at no extra charge: it isn't a separate paid add-on.
                <span className="mt-2 block text-amber-800" data-testid="ew-widget-isolation-status">
                  The embedded widget never completes a real reservation in-frame: it collects an inquiry and hands off to the hosted page, until a dedicated widget origin is in place.
                </span>
              </div>
            </>
          )}
        </div>
      )}

      {state.admin_disabled && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium">Disabled by ConfirmPh</div>
            <div className="text-xs">{state.admin_disable_reason}</div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <label className="text-xs font-medium text-slate-700">Website URL</label>
        <Input data-testid="ew-url" value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder="https://yourrental.com" />
        <label className="text-xs font-medium text-slate-700 mt-2 block">Button label</label>
        <select data-testid="ew-label"
                 className="w-full h-9 border rounded-md px-2 bg-white text-sm"
                 value={label} onChange={(e) => setLabel(e.target.value)}>
          {(state.allowed_labels || ["Visit official website"]).map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
        <div className="flex items-center gap-2 pt-2">
          <Button onClick={save} disabled={saving || !url}
                   data-testid="ew-save"
                   className="bg-emerald-700 hover:bg-emerald-800 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
            Save website
          </Button>
          {state.url && (
            <a href={state.url} target="_blank" rel="noopener noreferrer"
                data-testid="ew-open-link"
                className="text-xs inline-flex items-center gap-1 text-slate-600 hover:text-emerald-700">
              <ExternalLink className="w-3.5 h-3.5" /> Open
            </a>
          )}
          <StatusBadge status={state.verification_status} />
        </div>
        {error && <div className="text-xs text-red-700">{error}</div>}
        {success && <div className="text-xs text-emerald-700">{success}</div>}
      </div>

      {/* Plan-aware feature surface */}
      {!caps.can_show_public && (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"
              data-testid="ew-upsell-public">
          On the <strong>Free</strong> plan, the website is saved here but is
          not shown on your public ConfirmPh profile. Upgrade to <strong>Basic</strong>
          to add the “Visit official website” button.
        </div>
      )}

      {caps.can_track_clicks && (
        <div className="rounded-md border border-slate-200 bg-emerald-50 px-3 py-2 text-sm flex items-center justify-between"
              data-testid="ew-stats">
          <div>
            <div className="text-emerald-900 font-medium">
              ConfirmPh recorded approximately {state.clicks_this_month} external website CTA click{state.clicks_this_month === 1 ? "" : "s"} this month.
            </div>
            <div className="text-xs text-emerald-700/80">
              All-time recorded clicks: {state.clicks_total}. Counts are rate-limited and deduplicated estimates, not fraud-proof visitor totals.
            </div>
          </div>
        </div>
      )}

      {/* Verification: Pro+ */}
      <div className="rounded-md border border-slate-200 p-3 space-y-2"
            data-testid="ew-verification">
        <div className="font-medium text-sm flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-700" />
          Website ownership verification
        </div>
        {!caps.can_verify ? (
          <div className="text-xs text-slate-600" data-testid="ew-upsell-verify">
            Verification is a <strong>Pro</strong> feature. Verifying shows a
            “Website ownership verified” signal on your public profile and
            unlocks the ConfirmPh badge for your website. It does not
            mean ConfirmPh guarantees the business, vehicles, prices,
            documents, payments, refunds, insurance, or legal compliance.
          </div>
        ) : (
          <div className="space-y-2 text-xs text-slate-700">
            <ol className="list-decimal pl-5 space-y-1">
              <li>Click <strong>Start verification</strong> to get your tag.</li>
              <li>Paste the meta tag below into your website's <code>&lt;head&gt;</code>.</li>
              <li>Publish the change, then click <strong>Check now</strong>.</li>
            </ol>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={startVerify} disabled={verifying || !state.url}
                       data-testid="ew-verify-start">
                Start verification
              </Button>
              <Button size="sm" variant="outline" onClick={checkVerify}
                       disabled={verifying || !state.verification_token}
                       data-testid="ew-verify-check">
                Check now
              </Button>
            </div>
            {state.verification_token && (
              <div className="rounded bg-slate-900 text-emerald-100 font-mono text-[11px] p-2 break-all relative">
                {metaTag}
                <button onClick={() => copy(metaTag)} title="Copy"
                         data-testid="ew-copy-meta"
                         className="absolute top-1 right-1 text-emerald-200 hover:text-white">
                  {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Badge snippet: Pro+ */}
      {caps.can_show_badge && (
        <div className="rounded-md border border-slate-200 p-3 space-y-2"
              data-testid="ew-badge-snippet">
          <div className="font-medium text-sm">ConfirmPh badge for your website</div>
          <p className="text-xs text-slate-600">
            Paste this on your own website so customers can view your
            ConfirmPh profile, read booking reviews, or request booking
            verification.
          </p>
          <div className="rounded bg-slate-900 text-emerald-100 font-mono text-[11px] p-2 break-all relative">
            {badgeSnippet}
            <button onClick={() => copy(badgeSnippet)} title="Copy"
                     data-testid="ew-copy-badge"
                     className="absolute top-1 right-1 text-emerald-200 hover:text-white">
              {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>
          <p className="text-[10px] text-slate-500 italic">
            Wording allowed on your site: "Listed on ConfirmPh", "View our
            ConfirmPh profile", "Booking confirmation available through ConfirmPh".
            Avoid: "ConfirmPh approved", "guaranteed", "fully verified by ConfirmPh".
          </p>
        </div>
      )}

      <p className="text-[11px] text-slate-500 italic">
        External website notice: links open the rental business’s own
        website. ConfirmPh does not control external website content,
        prices, availability, payment instructions, policies, or offers.
      </p>
    </div>
  );
}

function StatusPill({ ok, label }) {
  return <span className={`px-2 py-1 rounded-full border ${ok ? "bg-emerald-100 border-emerald-200 text-emerald-800" : "bg-slate-100 border-slate-200 text-slate-600"}`}>{label}</span>;
}

function IntegrationCard({ icon: Icon, title, description, enabled, disabled, onToggle, testId, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-medium text-sm flex items-center gap-1.5"><Icon className="w-4 h-4 text-emerald-700" />{title}</div>
          <p className="text-xs text-slate-500 mt-1">{description}</p>
        </div>
        <button type="button" onClick={onToggle} disabled={disabled} data-testid={testId} aria-pressed={enabled} className={`px-2.5 py-1 rounded-full text-xs font-medium border disabled:opacity-50 ${enabled ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
          {enabled ? "Enabled" : "Disabled"}
        </button>
      </div>
      {children}
    </div>
  );
}

function CopyRow({ value, onCopy, label, testId, multiline = false }) {
  return (
    <div>
      <div className="text-[11px] font-medium text-slate-600 mb-1">{label}</div>
      <div className="relative rounded bg-slate-950 text-emerald-100 p-2 pr-8 font-mono text-[10px] break-all">
        {multiline ? <pre className="whitespace-pre-wrap">{value}</pre> : value}
        <button type="button" onClick={() => onCopy(value)} className="absolute top-2 right-2" data-testid={testId} aria-label={`Copy ${label}`}><Copy className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

function InstallGuide({ title, steps }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
      <div className="font-medium text-sm">{title}</div>
      <ol className="mt-1.5 list-decimal pl-4 text-xs text-slate-600 space-y-1">{steps.map((step) => <li key={step}>{step}</li>)}</ol>
    </div>
  );
}
